/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import isString from 'lodash.isstring';
import {
  getEntityId,
  getEntityUpdates,
  getIds,
  getSubscriptionResults
} from './helpers';
import Observers from './Observers';
import {getPreAuthToken} from './PreAuth';
import Socket from './Socket';
import * as logger from './SocketLogger';
import {
  EntitySubscription,
  InboundMessage,
  ObservableEntityIds,
  OnDataEvent,
  SocketConfiguration,
  SocketConnection,
  SubscriptionResponse
} from './types';

/**
 * The EntityObserver provides the single point of entry for sending and receiving
 * messages to and from an underlying WebSocket Endpoint.
 *
 * Generally, the `EntityObserver` API should only be used directly for initializing
 * the connection (via the `initialize`) method and for adding observers to the queue
 * via the `onInitialized` method.
 *
 * While the lower-level `EntityObserver` APIs can be used directly, the convention
 * is to implement higher-level entity specific APIs.
 *
 * ```typescript
 *   import EntityObserver from '@ion/services/src/websocket';
 *   import {get} from '@ion/core/src/environment/environment';
 *   import {getToken} from '@ion/core/src/services/SDConnectUtils';
 *
 *   EntityObserver.initialize({
 *    url       : get('websocketURL'),
 *    appKey    : get('appKey'),
 *    authToken : getToken()
 *   });
 * ```
 *
 */
class EntityObserver {
  /**
   * Provides the underlying WebSocket connection used for subscribing and receiving
   * updates.
   */
  private socket: SocketConnection = new Socket();

  /**
   * Provides a reference to the configuration options used to initialize the instance.
   */
  private configuration: SocketConfiguration;

  /**
   * Provides a reference to the subscription observers which are invoked when a
   * message which meets the criteria of the given observers is received.
   */
  private observers: Observers;

  /**
   * Provides a reference to the initialization callbacks which allow for queuing
   * observers prior to initialization.
   */
  private initializations: Function[];

  /**
   * Provides the state of the initialization of the instance, which is set once
   * the `initialize` method is invoked.
   */
  private initialized: boolean;

  /**
   * Provides a reference to the current subscription message pending status,
   * which is set to true when a subscription call is made, and false when
   * an unsubscribe call is made.
   */
  private isSubscriptionPending: boolean;

  /**
   * Provides the initialization entry point onto which the underlying Socket
   * connection is established. If the EntityObserver has not been previously
   * initialized, returns true, otherwise, returns false.
   *
   * Initialization requires asynchronously retrieving a Single Use Token which
   * is to be used for the initial WebSocket handshake and subsequent upgrade,
   * thus, invocations of `EntityObserver.initialize` should be awaited.
   *
   *
   * ```typescript
   *   import EntityObserver from '@ion/services/src/websocket';
   *   import {get} from '@ion/core/src/environment/environment';
   *   import {getToken} from '@ion/core/src/services/SDConnectUtils';
   *
   *   ...
   *
   *   await EntityObserver.initialize({
   *     url       : get('websocketURL'),
   *     appKey    : get('appKey'),
   *     authToken : getToken(),
   *     onConnect : event => {
   *       info('[CLIENT] onConnect callback invoked ...');
   *     },
   *     onDisconnect : event => {
   *       info('[CLIENT] onDisconnect callback invoked ...');
   *     },
   *     onConnectionError : event => {
   *       info('[CLIENT] onConnectionError callback invoked ...');
   *     },
   *     onReconnection : attempt => {
   *       info(`[CLIENT] onReconnection callback invoked [${attempt}] attempt ...`);
   *     }
   *   });
   * ```
   */
  async initialize(config: SocketConfiguration, force = false): Promise<boolean> {
    if (!this.initialized || force) {
      this.observers       = new Observers();
      this.initializations = [];
      this.configuration   = config;
      this.initialized     = true;
      try {
        const preauthToken = await getPreAuthToken(config.authToken);
        logger.preauthSucceeded();
        this.socket.initialize({
          onConnect         : this.onConnectionEstablished,
          onDisconnect      : this.onConnectionClosed,
          onConnectionError : this.onConnectionError,
          onData            : this.onDataReceived,
          onReconnection    : this.onReconnection,
          preauthToken,
          ...config
        }, force);
      } catch (err) {
        logger.preauthFailed();
      }
    }
    return this.initialized;
  }

  /**
   * Provides a utility which allows for invoking a callback once the underlying
   * connection has been established. If the connection is currently open, the
   * callback will be invoked immediately, otherwise, it wil be added to the
   * queue and invoked upon the first successful connect.
   *
   * @param   callback
   * @returns true if the EntityObserver has been initialized, otherwise false,
   *          in which case the callback will be invoked once the EntityObserver
   *          has been initialized
   */
  onInitialized(callback): boolean {
    if (!this.isInitialized) {
      this.initializations.push(callback);
      return false;
    }
    callback();
    return true;
  }

  /**
   * Provides a convenience which determines if the EntityObserver underlying
   * Socket connection has been established; thus, the EntityObserver has been
   * initialized, otherwise, initialization is pending.
   */
  get isInitialized(): boolean {
    return !!this.initialized;
  };

  /**
   * Provides a convenience which determines if the Socket is currently connected.
   *
   * ```typescript
   *   if (EntityObserver.isConnected) {
   *     ...
   *   }
   * ```
   *
   * @returns true if the underlying WebSocket is connected, otherwise, false.
   */
  get isConnected(): boolean {
    return !!this.socket.isConnected;
  };

  /**
   * Allows for subscribing to an entity or collection of entities for changes.
   * Generally, clients are not to use this method directly, but rather, the
   * higher-level APIs should be used for adding observers.
   *
   * ```typescript
   *   const active: boolean = EntityObserver.subscribe(
   *     EntitySubscription.asset, asset.id, onAssetUpdate
   *   );
   * ```
   *
   * @param   entity The type of entity for which the subscription is to be made.
   * @param   ids A single, or multiple ids for which to observe for changes.
   * @param   observer A callback function which will be invoked when a change is
   *          received for the given ids.
   * @returns True if the subscription was successfully created, otherwise, false.
   *          A subscription will always be sent unless the connection has yet to
   *          have been established, or if the underlying websocket call fails.
   */
  subscribe(entity: EntitySubscription, ids: ObservableEntityIds, observer: OnDataEvent): boolean {
    const identifiers = getIds(ids);
    this.observers.addObserver(entity, identifiers, observer);
    this.isSubscriptionPending = true;
    return this.socket.subscribe(entity, identifiers);
  };

  /**
   * Allows for unsubscribing an entity or collection of entities from receiving
   * change notifications. Generally, clients are not to use this method directly,
   * but rather, the higher-level APIs should be used for removing observers.
   *
   * Multiple observers can be added for the same entity. As such, the underlying
   * "unsubscribe" message will only be sent if there are no remaining observers
   * for the given entity id. Otherwise, the provided observer will be removed
   * from the list of observers for the entity, and the remaining observers will
   * continue to receive change notifications.
   *
   * ```typescript
   *   const active: boolean = EntityObserver.unsubscribe(
   *     EntitySubscription.asset, asset.id, onAssetUpdate
   *   );
   * ```
   * @param   entity The type of entity for which the subscription is to be removed.
   * @param   ids A single, or multiple ids for which observers are to be removed.
   * @param   observer The callback function to be removed from change observations.
   * @returns True if there are no observers remaining for the given id and the
   *          subscription was successfully removed, or if there are additional
   *          observers remaining and the specified observer has been removed.
   *          Otherwise, false if there are no observers remaining and the
   *          unsubscribe message has failed.
   */
  unsubscribe(entity: EntitySubscription, ids: ObservableEntityIds, observer: OnDataEvent): boolean {
    const identifiers = getIds(ids);
    const results     = this.observers.removeObserver(entity, identifiers, observer);
    const obsoletions = Observers.getObsoleteIds(results);
    this.isSubscriptionPending = false;
    if (obsoletions.length) {
      return this.socket.unsubscribe(entity, obsoletions);
    }
    return true;
  };

  /**
   * Allows for forced removals of all subscriptions and observers for the given entity
   * id(s).
   *
   * ```typescript
   *   // remove all observers and the subscriptions for the given asset ids ...
   *   EntityObserver.removeSubscriptions(EntitySubscription.asset, assetIds);
   * ```
   *
   * @param   entity The type of entity for which the subscriptions are to be removed.
   * @param   ids A single, or multiple ids for which all observers are to be removed.
   * @returns True if the subscription was successfully removed, otherwise, false
   *          the unsubscribe message has failed.
   */
  removeSubscriptions(entity: EntitySubscription, ids: ObservableEntityIds): boolean {
    const identifiers = getIds(ids);
    this.isSubscriptionPending = false;
    this.observers.removeObserver(entity, identifiers);
    return this.socket.unsubscribe(entity, identifiers);
  };

  /**
   * Allows for forced removals of all subscriptions and observers for the entity
   * type.
   *
   * ```typescript
   *   // remove all asset observers and subscriptions ...
   *   EntityObserver.removeAllSubscriptions(EntitySubscription.asset);
   * ```
   *
   * @param   entity The type of entity for which all subscriptions and observers
   *          are to be removed.
   * @returns True if the subscription was successfully removed, otherwise, false
   *          the unsubscribe message has failed.
   */
  removeAllSubscriptions(entity: EntitySubscription): boolean {
    const identifiers = this.observers.getObservedIds(entity);
    this.observers    = new Observers();
    this.isSubscriptionPending = false;
    return this.socket.unsubscribe(entity, identifiers);
  };

  /**
   * Invoked when a message is received from the underlying WebSocket Connection
   * (via the `Socket` instance member). Upon receiving a message, the API will
   * invoke all observers which have subscribed to changes for the received
   * message. If the message is a SubscriptionResponse, then the invokeObservers
   * callback will be invoked with the successfully added subscription entity
   * ids, as well as those which have failed to be added (if any).
   *
   * The onDataReceived listener handles three distinct types of messages, which
   * are as follows:
   *
   *  - Entity Updates: These messages are received when a change occurs to
   *    a subscribed entity.
   *  - Subscription Updates: These messages are received when an entity is
   *    added or removed from the update subscriptions.
   *  - Ping Updates: These messages are received when a ping message has
   *    been sent and a response has been returned.
   *
   */
  private onDataReceived = (incoming: InboundMessage | SubscriptionResponse): void => {
    try {
      const id = getEntityId(incoming as InboundMessage);
      this.configuration.onData?.(incoming, id);
      if (!id) {
        if (!isString(incoming.message)) {
          const results = getSubscriptionResults(incoming as SubscriptionResponse);
          this.configuration.onSubscription?.(results);
          logger.subscription(results, this.isSubscriptionPending, this.socket);
        } else {
          logger.keepalive(this.socket);
        }
      } else {
        this.observers.invokeObservers(id, getEntityUpdates(incoming as InboundMessage));
      }
    } catch (err) {
      logger.processingException(err, this.socket);
    }
  };

  /**
   * Invoked when the underlying WebSocket Connection has been successfully
   * established (connected), at which point all initialization callbacks
   * are invoked.
   */
  private onConnectionEstablished = (event: Event): void => {
    this.configuration.onConnect?.(event);
    this.initializations.forEach(client => {
      client();
    });
    this.initializations = [];
  };

  /**
   * Invoked when the underlying Web Socket has been closed, at which point if
   * the `onDisconnect` option was provided to the `initialization` call, it
   * will be invoked.
   */
  private onConnectionClosed = (event: CloseEvent): void => {
    this.configuration.onDisconnect?.(event);
  };

  /**
   * Invoked when the underlying Web Socket throws an exception, at which point
   * if the `onConnectionError` option was provided to the `initialization` call,
   * it will be invoked.
   */
  private onConnectionError = (event: ErrorEvent): void => {
    this.configuration.onConnectionError?.(event);
  };

  /**
   * Invoked when the underlying Web Socket connection has been re-established,
   * at which point the `onReconnection` option provided to the `initialization`
   * call will be invoked (if provided).
   */
  private onReconnection = (attempt: number): void => {
    this.configuration.onReconnection?.(attempt);
  };
};

export default new EntityObserver();
